'use client';

import CheckAudio from 'components/CheckAudio';
import BEQLookupForm from 'components/forms/BEQLookupForm';
import ProviderForm from 'components/forms/doctors/ProviderForm';
import IncomeForm from 'components/forms/IncomeForm';
import InviteForm from 'components/forms/InviteForm';
import LocationForm from 'components/forms/LocationForm';
import PharmacyForm from 'components/forms/pharmacies/PharmacyForm';
import DrugForm from 'components/forms/prescriptions/DrugForm';
import SEPForm from 'components/forms/SEPForm';
import RecommendationPage from 'components/recommendation/RecommendationPage';
import RecommendationTrigger from 'components/recommendation/RecommendationTrigger';
import ExplanationComponent from 'components/explanation/ExplanationComponent';
import AlexIdQuestionComponent from 'components/alex-id/AlexIdQuestionComponent';
import ContentComponent from 'components/content/ContentComponent';
import LibraryComponent from 'components/library/LibraryComponent';
import SingleQuestionComponent from 'components/single-question/SingleQuestionComponent';
import { FormattedComponentPage } from 'types/kontentAi';

import VisitsForm from 'components/forms/VisitsForm';
import AdditionalMedicalPlanForm from 'components/forms/AdditionalMedicalPlanForm';
import AudioPlayer from 'components/media/AudioPlayer';
import { useState } from 'react';
import { Destination } from 'types/content';
import Pathfinder from 'components/pathfinder/Pathfinder';
import LongFormEducation from 'components/education/LongFormEducation';
import { usePlayAudio } from 'hooks/usePlayAudio';
import BrowsePlansCta from 'components/BrowsePlansCta';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import styles from './component_page.module.scss';

const COMPONENT_MAP = {
  location_form: LocationForm,
  drug_form: DrugForm,
  pharmacy_form: PharmacyForm,
  provider_form: ProviderForm,
  recommendation: RecommendationPage,
  check_audio: CheckAudio,
  beq_form: BEQLookupForm,
  income_form: IncomeForm,
  invite_form: InviteForm,
  sep_form: SEPForm,
  recommendation_trigger: RecommendationTrigger,
  explanation: ExplanationComponent,
  alex_id_question: AlexIdQuestionComponent,
  content: ContentComponent,
  library: LibraryComponent,
  single_question: SingleQuestionComponent,
  visits_form: VisitsForm,
  additional_medical_plan_form: AdditionalMedicalPlanForm,
  long_form_education: LongFormEducation,
};

const ComponentPage = ({
  components,
  destination,
  bridgeAudio,
  responseAudio,
}: FormattedComponentPage['content']) => {
  const [readyToContinue, setReadyToContinue] = useState<boolean>(false);
  const [destinations, setDestinations] = useState<Destination[]>(destination);

  const [currentAudioSource, setCurrentAudioSource] = useState<string>(
    bridgeAudio?.mp3File.url || ''
  );
  const [currentAudioType, setCurrentAudioType] = useState<
    'bridge' | 'response' | 'step'
  >('bridge');
  const [currentAudioCaptions, setCurrentAudioCaptions] = useState<string>(
    bridgeAudio?.closedCaption?.url || ''
  );

  const pathname = usePathname();

  const isCheckAudioPage = pathname.startsWith('/check-audio');

  const playAudio = usePlayAudio({
    setCurrentAudioSource,
    setCurrentAudioType,
    setCurrentAudioCaptions,
  });

  const handleContinue = () => {
    setReadyToContinue(true);
  };

  const onContinue = () => {
    if (!playAudio(responseAudio)) {
      handleContinue();
    }
  };

  return (
    <div className="conversation">
      <div
        className={classNames(
          styles.contentWrapper,
          isCheckAudioPage && styles.checkAudioWrapper
        )}
      >
        {components.map((component, idx) => {
          const type = component.system.type;
          const Component = COMPONENT_MAP[type as keyof typeof COMPONENT_MAP];

          return (
            <Component
              key={idx}
              data={component}
              onContinue={onContinue}
              setCurrentAudioSource={setCurrentAudioSource}
              setCurrentAudioType={setCurrentAudioType}
              setCurrentAudioCaptions={setCurrentAudioCaptions}
              setDestinations={setDestinations}
            />
          );
        })}

        {!pathname.startsWith('/browse') && <BrowsePlansCta />}
      </div>

      {currentAudioSource && (
        <AudioPlayer
          source={currentAudioSource}
          track={currentAudioCaptions}
          type={currentAudioType}
          onContinue={handleContinue}
        />
      )}
      {readyToContinue && <Pathfinder destinations={destinations} />}
    </div>
  );
};

export default ComponentPage;
